import { Outlet } from 'react-router-dom';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { DashboardSidebar } from './DashboardSidebar';
import { useLastSeen } from '@/hooks/useLastSeen';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLoginSummary } from '@/hooks/useLoginSummary';
import { LoginSummaryDialog } from './LoginSummaryDialog';
import nexaboardIcon from '@/assets/nexaboard-icon.png';
import { Link } from 'react-router-dom';
import { memo, useState, useEffect } from 'react';
import { useDashboardPrefetch } from '@/hooks/useDashboardPrefetch';

// Memoized sidebar to prevent re-renders on route changes
const MemoizedSidebar = memo(DashboardSidebar);
MemoizedSidebar.displayName = 'MemoizedSidebar';

export const DashboardLayoutRoute = () => {
  const isMobile = useIsMobile();
  useDashboardPrefetch();
  
  // Defer non-critical hooks to not block initial render
  const [deferredReady, setDeferredReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setDeferredReady(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="min-h-svh flex w-full bg-background text-foreground overflow-x-hidden dashboard-scope">
        <MemoizedSidebar />
        <SidebarInset className="flex flex-col min-w-0 w-full">
          {/* Mobile Header with Menu Button */}
          {isMobile && (
            <header className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-card/50 shrink-0 sticky top-0 z-30">
              <SidebarTrigger className="h-8 w-8" />
              <Link to="/dashboard" className="flex items-center gap-2">
                <img src={nexaboardIcon} alt="NexaBoard" className="h-7 w-7 object-contain" />
                <span className="font-bold text-sm bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">NexaBoard</span>
              </Link>
            </header>
          )}
          <main className={isMobile 
            ? "flex-1 px-3 py-3 overflow-x-hidden overflow-y-auto" 
            : "flex-1 p-6 overflow-x-hidden"
          }>
            <div className="w-full max-w-full">
              <Outlet />
            </div>
          </main>
        </SidebarInset>
      </div>

      {/* Deferred hooks - load after initial paint */}
      {deferredReady && <DeferredHooks />}
    </SidebarProvider>
  );
};

// Separate component for deferred hooks to avoid re-rendering the layout
const DeferredHooks = memo(() => {
  useLastSeen();
  const { summary, shouldShow, markAsSeen } = useLoginSummary();
  
  return (
    <LoginSummaryDialog 
      open={shouldShow} 
      onClose={markAsSeen} 
      summary={summary} 
    />
  );
});
DeferredHooks.displayName = 'DeferredHooks';
